
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Swal from "sweetalert2";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
    Divider,
} from "@mui/material";
import {
    SchoolRounded,
    EmailRounded,
    LockRounded,
    VisibilityRounded,
    VisibilityOffRounded,
    ArrowForwardRounded,
    ArrowBackRounded,
    VerifiedRounded,
    AutoAwesomeRounded,
    MenuBookRounded,
    WorkspacePremiumRounded,
    SecurityRounded,
    BoltRounded,
} from "@mui/icons-material";

import api from "../api/api";
import { AuthContext } from "../AuthContext";
import student from "./schoolmanagementsystem.png";

const C = {
    navy: "#07152F",
    navyLight: "#10294D",
    blue: "#087EA4",
    blueLight: "#DFF5FC",
    bluePale: "#F1FAFD",
    orange: "#F97316",
    orangePale: "#FFF0E5",
    white: "#FFFFFF",
    ink: "#12243A",
    muted: "#738296",
    border: "#E1EAF0",
    green: "#15966A",
};

const BRAND = "Jana L. Williams Educational Foundation";

const css = `
.sicx-root {
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    color: ${C.ink};
    background: #f5f8fc;
    font-family: Inter, "Segoe UI", Arial, sans-serif;
    overflow: hidden;
}
.sicx-root * { box-sizing: border-box; }
.sicx-root a { text-decoration: none; }
.sicx-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.06fr) minmax(440px, .94fr);
    min-height: 100vh;
    min-height: 100dvh;
}
.sicx-showcase {
    position: relative;
    isolation: isolate;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
    overflow: hidden;
    padding: 35px clamp(26px, 4vw, 65px) 25px;
    color: white;
    background:
        radial-gradient(ellipse at 15% 10%, rgba(8,126,164,.35), transparent 34%),
        radial-gradient(ellipse at 88% 80%, rgba(249,115,22,.12), transparent 31%),
        linear-gradient(145deg, #07152f 0%, #071d3a 52%, #03263b 100%);
}
.sicx-showcase:before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: .14;
    background-image: radial-gradient(rgba(255,255,255,.5) .7px, transparent .7px);
    background-size: 21px 21px;
    mask-image: linear-gradient(140deg, black, transparent 76%);
}
.sicx-showcase:after {
    content: "";
    position: absolute;
    width: 520px;
    height: 520px;
    right: -330px;
    top: 17%;
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 50%;
    box-shadow: 0 0 0 45px rgba(255,255,255,.025), 0 0 0 90px rgba(255,255,255,.02);
    pointer-events: none;
}
.sicx-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 3;
}
.sicx-brand-mark {
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,.23);
    border-radius: 16px;
    background: linear-gradient(145deg, #159bc1, #056083);
    box-shadow: 0 9px 25px rgba(0,0,0,.22);
}
.sicx-brand-title {
    color: #fff;
    font-size: 13px;
    font-weight: 900;
    letter-spacing: .7px;
    line-height: 1.5;
}
.sicx-brand-subtitle {
    color: #a8d9e9;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
}
.sicx-hero {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 680px;
    margin: 38px auto 28px;
}
.sicx-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid rgba(255,255,255,.17);
    border-radius: 50px;
    background: rgba(255,255,255,.065);
    color: #c6effa;
    font-size: 10px;
    font-weight: 850;
    letter-spacing: 1.6px;
}
.sicx-hero-heading {
    margin: 22px 0 0 !important;
    max-width: 640px;
    color: #fff;
    font-size: clamp(37px, 4.3vw, 65px) !important;
    font-weight: 950 !important;
    letter-spacing: -2.5px !important;
    line-height: 1.04 !important;
}
.sicx-hero-heading span {
    color: #7de1f6;
}
.sicx-hero-description {
    max-width: 470px;
    margin-top: 18px !important;
    color: #b7c9dd;
    font-size: 14px !important;
    line-height: 1.9 !important;
}
.sicx-visual {
    position: relative;
    min-height: 250px;
    height: clamp(250px, 35vh, 390px);
    margin-top: 12px;
}
.sicx-visual-halo {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(85%, 410px);
    aspect-ratio: 1;
    transform: translate(-50%, -48%);
    border-radius: 50%;
    background: radial-gradient(circle, rgba(14,130,173,.32), rgba(8,39,70,.2) 54%, transparent 72%);
    border: 1px solid rgba(125,225,246,.13);
    box-shadow: 0 0 0 28px rgba(255,255,255,.018), 0 0 0 62px rgba(255,255,255,.012);
}
.sicx-student {
    position: absolute;
    z-index: 2;
    width: min(83%, 470px);
    height: 100%;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    object-fit: contain;
    object-position: center bottom;
    filter: drop-shadow(0 25px 24px rgba(0,0,0,.25));
}
.sicx-floating {
    position: absolute;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 12px 15px;
    border: 1px solid rgba(255,255,255,.19);
    border-radius: 15px;
    color: #fff;
    background: rgba(11,34,61,.78);
    backdrop-filter: blur(14px);
    box-shadow: 0 15px 40px rgba(0,0,0,.17);
}
.sicx-floating-icon {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 11px;
    color: #9de9fa;
    background: rgba(8,126,164,.2);
}
.sicx-floating-label {
    color: #a9bdcf;
    font-size: 10px;
    font-weight: 700;
}
.sicx-floating-value {
    margin-top: 3px;
    color: white;
    font-size: 12px;
    font-weight: 850;
}
.sicx-float-left { left: 0; top: 17%; }
.sicx-float-right { right: 0; bottom: 14%; }
.sicx-showcase-footer {
    position: relative;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding-top: 18px;
    border-top: 1px solid rgba(255,255,255,.13);
}
.sicx-footer-text {
    color: #a6b8ca;
    font-size: 10px;
    line-height: 1.7;
}
.sicx-footer-highlight {
    color: #f4a36e;
    font-weight: 850;
}
.sicx-form-side {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 0;
    min-height: 100vh;
    padding: 35px clamp(24px, 4vw, 68px);
    overflow: hidden;
    background:
        radial-gradient(ellipse at 100% 0%, rgba(8,126,164,.08), transparent 34%),
        linear-gradient(155deg, #fbfdff 0%, #f4f8fc 100%);
}
.sicx-form-side:before {
    content: "";
    position: absolute;
    width: 290px;
    height: 290px;
    left: -210px;
    bottom: -160px;
    border: 1px solid rgba(8,126,164,.11);
    border-radius: 50%;
    box-shadow: 0 0 0 32px rgba(8,126,164,.025), 0 0 0 65px rgba(8,126,164,.02);
    pointer-events: none;
}
.sicx-form-inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 425px;
    margin: auto;
}
.sicx-form-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 31px;
}
.sicx-portal-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #52677d;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 1.2px;
}
.sicx-online {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #15815f;
    font-size: 10px;
    font-weight: 800;
}
.sicx-online-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #1ab27c;
    box-shadow: 0 0 0 4px rgba(26,178,124,.1);
}
.sicx-form-heading {
    margin: 0 !important;
    color: ${C.navy};
    font-size: clamp(30px, 3vw, 39px) !important;
    font-weight: 950 !important;
    letter-spacing: -1.4px !important;
    line-height: 1.16 !important;
}
.sicx-form-description {
    margin-top: 11px !important;
    color: #748499;
    font-size: 13px !important;
    line-height: 1.8 !important;
}
.sicx-field {
    margin-top: 22px !important;
}
.sicx-field .MuiInputLabel-root {
    color: #718197;
    font-size: 13px;
    font-weight: 600;
}
.sicx-field .MuiOutlinedInput-root {
    min-height: 56px;
    border-radius: 13px;
    background: rgba(255,255,255,.91);
    color: #16283d;
    font-size: 13px;
    transition: box-shadow .2s ease, background .2s ease;
}
.sicx-field .MuiOutlinedInput-notchedOutline {
    border-color: #dce6ee;
}
.sicx-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: #9acddd;
}
.sicx-field .MuiOutlinedInput-root.Mui-focused {
    background: #fff;
    box-shadow: 0 0 0 4px rgba(8,126,164,.075);
}
.sicx-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #087ea4;
    border-width: 1.5px;
}
.sicx-field .MuiInputLabel-root.Mui-focused {
    color: #087ea4;
}
.sicx-submit {
    min-height: 55px !important;
    border-radius: 13px !important;
    color: white !important;
    background: linear-gradient(105deg, #071b3e 0%, #075e81 70%, #087ea4 100%) !important;
    font-size: 13px !important;
    font-weight: 900 !important;
    text-transform: none !important;
    box-shadow: 0 12px 25px rgba(7,76,112,.18) !important;
    transition: transform .2s ease, box-shadow .2s ease !important;
}
.sicx-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 30px rgba(7,76,112,.24) !important;
}
.sicx-submit:disabled {
    color: white !important;
    background: #8aa6b5 !important;
    box-shadow: none !important;
}
.sicx-home-button {
    color: #66788d !important;
    font-size: 12px !important;
    font-weight: 750 !important;
    text-transform: none !important;
}
.sicx-home-button:hover { background: rgba(8,126,164,.06) !important; }
.sicx-trust-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border: 1px solid #e0ebf1;
    border-radius: 13px;
    background: rgba(255,255,255,.7);
}
.sicx-trust-icon {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    display: grid;
    place-items: center;
    border-radius: 11px;
    color: #087ea4;
    background: #e4f7fc;
}
.sicx-trust-title {
    color: #21374b;
    font-size: 11px;
    font-weight: 850;
}
.sicx-trust-copy {
    margin-top: 3px;
    color: #8290a0;
    font-size: 10px;
    line-height: 1.6;
}
.sicx-mobile-brand { display: none; }

@media (max-width: 1050px) {
    .sicx-layout { grid-template-columns: minmax(0, 1fr) minmax(390px, .95fr); }
    .sicx-showcase { padding-left: 30px; padding-right: 30px; }
    .sicx-hero-heading { font-size: clamp(36px, 4.5vw, 52px) !important; }
    .sicx-floating { padding: 10px; }
    .sicx-float-left { left: -5px; }
    .sicx-float-right { right: -5px; }
}
@media (max-width: 800px) {
    .sicx-root { overflow: auto; }
    .sicx-layout { display: block; min-height: 100vh; min-height: 100dvh; }
    .sicx-showcase { display: none; }
    .sicx-mobile-brand {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px 19px;
        color: white;
        background: linear-gradient(115deg, #07152f, #073755);
    }
    .sicx-mobile-brand .sicx-brand-mark { width: 42px; height: 42px; flex-basis: 42px; }
    .sicx-mobile-portal {
        padding: 7px 10px;
        border: 1px solid rgba(255,255,255,.2);
        border-radius: 30px;
        color: #c5eefa;
        font-size: 9px;
        font-weight: 850;
        letter-spacing: .6px;
        white-space: nowrap;
    }
    .sicx-form-side {
        min-height: auto;
        padding: 31px 21px 24px;
        overflow: visible;
        background: linear-gradient(180deg, #f1f8fc 0%, #fbfdff 100%);
    }
    .sicx-form-inner { max-width: 470px; }
    .sicx-form-topline { margin-bottom: 25px; }
    .sicx-form-heading { font-size: 34px !important; }
}
@media (max-width: 420px) {
    .sicx-mobile-brand { padding: 13px 12px; }
    .sicx-brand-title { font-size: 11px; }
    .sicx-brand-subtitle { font-size: 9px; letter-spacing: 1.4px; }
    .sicx-mobile-portal { font-size: 8px; padding: 7px 8px; }
    .sicx-form-side { padding: 25px 15px 22px; }
    .sicx-form-heading { font-size: 30px !important; }
    .sicx-form-topline { margin-bottom: 23px; }
}
@media (prefers-reduced-motion: reduce) {
    .sicx-root *, .sicx-root *::before, .sicx-root *::after {
        animation-duration: .01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: .01ms !important;
        scroll-behavior: auto !important;
    }
}
`;

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);
    const reduceMotion = useReducedMotion();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const clearAuthenticationData = () => {
        [
            "access",
            "refresh",
            "user",
            "employment_status",
            "employment_status_display",
            "employment_academic_year",
            "auth_email",
        ].forEach((key) => localStorage.removeItem(key));
    };

    const redirectUser = (user) => {
        const role = String(user?.user_type || user?.role || "")
            .toLowerCase()
            .trim();

        const routes = {
            owner: "/owner-dashboard",
            admin: "/newAdminDashboard",
            teacher: "/teacher-dashboard",
            student: "/student-dashboard",
            parent: "/parentsDashboard",
            registry: "/registerDashboard",
            principal: "/principal-dashboard",
            proprietor: "/proprietor-dashboard",
            vpi: "/vpi-dashboard",
            vpa: "/newVpaDashboard",
            "it personel": "/it-dashboard",
            "it personnel": "/it-dashboard",
            business_manager: "/businessManagerDashboard",
            "business manager": "/businessManagerDashboard",
            "admission officer": "/admissionOfficialDashboard",
            admission_officer: "/admissionOfficialDashboard",
            dean: "/dean-dashboard",
        };

        navigate(routes[role] || "/");
    };

    const showEmploymentStatusAlert = (data) => {
        const code = String(data?.error_code || "").toUpperCase().trim();
        const status = String(data?.employment_status || "").toLowerCase().trim();
        const year = String(
            data?.academic_year ||
            data?.academic_year_name ||
            "the current academic year"
        );

        const fire = (options) =>
            Swal.fire({
                confirmButtonColor: C.orange,
                background: "#fff",
                ...options,
            });

        if (
            ["TEACHER_FIRED", "EMPLOYMENT_FIRED"].includes(code) ||
            status === "fired"
        ) {
            return fire({
                icon: "error",
                title: "Account Access Restricted",
                text: "Your employment record indicates that your employment has been terminated. Please contact the school administration for assistance.",
            });
        }

        if (
            ["TEACHER_REMOVED", "EMPLOYMENT_REMOVED"].includes(code) ||
            status === "removed"
        ) {
            return fire({
                icon: "warning",
                title: "Account Access Restricted",
                text: "Your employment record indicates that you have been removed from the school. Please contact the school administration for assistance.",
            });
        }

        if (code === "TEACHER_NOT_ASSIGNED") {
            return fire({
                icon: "warning",
                title: "Teacher Not Assigned",
                text: `Your teacher account is not assigned to an active academic year. Academic year: ${year}. Please contact the school administration.`,
            });
        }

        if (code === "NO_ACTIVE_ACADEMIC_YEAR") {
            return fire({
                icon: "warning",
                title: "No Active Academic Year",
                text: "There is currently no active academic year. Please contact the school administration.",
            });
        }

        if (
            ["EMPLOYMENT_LOGIN_BLOCKED", "LOGIN_BLOCKED"].includes(code)
        ) {
            return fire({
                icon: "warning",
                title: "Login Blocked",
                text: "Your account is currently blocked from accessing the school management system. Please contact an authorized administrator.",
            });
        }

        return Promise.resolve();
    };

    const handleBlockedResponse = (data) => {
        if (!data) return false;

        const status = String(data?.employment_status || "").toLowerCase().trim();
        const code = String(data?.error_code || "").toUpperCase().trim();

        const codes = [
            "TEACHER_REMOVED",
            "TEACHER_FIRED",
            "EMPLOYMENT_REMOVED",
            "EMPLOYMENT_FIRED",
            "EMPLOYMENT_LOGIN_BLOCKED",
            "LOGIN_BLOCKED",
            "TEACHER_NOT_ASSIGNED",
            "NO_ACTIVE_ACADEMIC_YEAR",
        ];

        if (
            !codes.includes(code) &&
            !["removed", "fired"].includes(status)
        ) {
            return false;
        }

        clearAuthenticationData();
        showEmploymentStatusAlert(data);
        return true;
    };

    const getLoginError = (error) => {
        const data = error?.response?.data;

        if (data?.detail) return String(data.detail);
        if (data?.message) return String(data.message);
        if (typeof data === "string") return data;

        if (data && typeof data === "object") {
            const messages = [];

            Object.entries(data).forEach(([key, value]) => {
                if (key === "detail") return;

                if (Array.isArray(value)) {
                    value.forEach((item) => messages.push(String(item)));
                } else if (typeof value === "string") {
                    messages.push(value);
                }
            });

            if (messages.length) return messages.join("\n");
        }

        return "Invalid email or password. Please check your details and try again.";
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail || !password) {
            await Swal.fire({
                icon: "warning",
                title: "Complete Your Login",
                text: "Please enter your email address and password.",
                confirmButtonColor: C.orange,
            });
            return;
        }

        setLoading(true);

        try {
            clearAuthenticationData();

            const response = await api.post("/api/token/", {
                email: cleanEmail,
                password,
            });

            if (handleBlockedResponse(response.data)) return;

            const role = String(
                response.data?.user_type ||
                response.data?.user?.role ||
                ""
            ).toLowerCase().trim();

            const authenticationRoles = [
                "admin",
                "registry",
                "principal",
                "proprietor",
                "business_manager",
                "business manager",
                "vpi",
                "vpa",
                "it personel",
                "it personnel",
            ];

            if (
                response.data?.requires_authentication &&
                authenticationRoles.includes(role)
            ) {
                const authEmail =
                    response.data?.email ||
                    response.data?.user?.email ||
                    cleanEmail;

                localStorage.setItem("auth_email", authEmail);

                navigate("/loginAuthentication", {
                    state: { email: authEmail },
                });
                return;
            }

            const { access, refresh } = response.data || {};

            if (!access || !refresh) {
                throw new Error("Authentication tokens were not returned.");
            }

            localStorage.setItem("access", access);
            localStorage.setItem("refresh", refresh);

            if (response.data?.employment_status) {
                localStorage.setItem(
                    "employment_status",
                    response.data.employment_status
                );
            }

            if (response.data?.employment_status_display) {
                localStorage.setItem(
                    "employment_status_display",
                    response.data.employment_status_display
                );
            }

            if (response.data?.academic_year) {
                localStorage.setItem(
                    "employment_academic_year",
                    response.data.academic_year
                );
            }

            window.dispatchEvent(new Event("user-logged-in"));

            let profileResponse;

            try {
                profileResponse = await api.get("/api/user-profile/");
            } catch (profileError) {
                clearAuthenticationData();
                throw profileError;
            }

            const user = profileResponse.data;

            const employmentStatus = String(
                response.data?.employment_status ||
                user?.employment_status ||
                ""
            ).toLowerCase().trim();

            if (["removed", "fired"].includes(employmentStatus)) {
                clearAuthenticationData();

                await showEmploymentStatusAlert({
                    error_code:
                        employmentStatus === "removed"
                            ? "TEACHER_REMOVED"
                            : "TEACHER_FIRED",
                    employment_status: employmentStatus,
                });

                return;
            }

            setUser(user);
            localStorage.setItem("user", JSON.stringify(user));

            await Swal.fire({
                icon: "success",
                title: "Welcome Back!",
                text: `Welcome ${user?.user_type || user?.role || ""}`,
                timer: 1600,
                showConfirmButton: false,
                background: "#fff",
                iconColor: C.green,
            });

            redirectUser(user);
        } catch (error) {
            console.error("LOGIN ERROR:", error);

            if (handleBlockedResponse(error?.response?.data)) return;

            await Swal.fire({
                icon: "error",
                title: "Unable to Sign In",
                text: getLoginError(error),
                confirmButtonText: "Try Again",
                confirmButtonColor: C.orange,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="sicx-root">
            <style>{css}</style>

            <Box className="sicx-layout">
                {/* DESKTOP BRAND EXPERIENCE */}
                <Box className="sicx-showcase">
                    <Box className="sicx-brand">
                        <Box className="sicx-brand-mark">
                            <SchoolRounded sx={{ fontSize: 28 }} />
                        </Box>

                        <Box>
                            <Typography className="sicx-brand-title">
                                Jana L. Williams
                            </Typography>
                            <Typography className="sicx-brand-subtitle">
                               Educational Foundation
                            </Typography>
                        </Box>
                    </Box>

                    <Box className="sicx-hero">
                        <motion.div
                            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55 }}
                        >
                            <Box className="sicx-eyebrow">
                                <AutoAwesomeRounded sx={{ fontSize: 15, color: C.orange }} />
                                EDUCATION WITH PURPOSE
                            </Box>

                            <Typography className="sicx-hero-heading">
                                Building minds.
                                <br />
                                Shaping <span>futures.</span>
                            </Typography>

                            <Typography className="sicx-hero-description">
                                A connected school community where learning,
                                character, and excellence prepare every student
                                for a brighter tomorrow.
                            </Typography>
                        </motion.div>

                        <Box className="sicx-visual">
                            <Box className="sicx-visual-halo" />

                            <motion.img
                                src={student}
                                alt="Student representing academic achievement"
                                className="sicx-student"
                                initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.75, delay: 0.12 }}
                            />

                            <motion.div
                                className="sicx-floating sicx-float-left"
                                animate={reduceMotion ? {} : { y: [0, -7, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <Box className="sicx-floating-icon">
                                    <MenuBookRounded />
                                </Box>
                                <Box>
                                    <Typography className="sicx-floating-label">
                                        YOUR JOURNEY
                                    </Typography>
                                    <Typography className="sicx-floating-value">
                                        Starts with learning
                                    </Typography>
                                </Box>
                            </motion.div>

                            <motion.div
                                className="sicx-floating sicx-float-right"
                                animate={reduceMotion ? {} : { y: [0, 7, 0] }}
                                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <Box
                                    className="sicx-floating-icon"
                                    sx={{
                                        color: "#ffd2b3",
                                        background: "rgba(249,115,22,.18)",
                                    }}
                                >
                                    <WorkspacePremiumRounded sx={{ color: "#ffb37f" }} />
                                </Box>
                                <Box>
                                    <Typography className="sicx-floating-label">
                                        OUR COMMITMENT
                                    </Typography>
                                    <Typography className="sicx-floating-value">
                                        Character & excellence
                                    </Typography>
                                </Box>
                            </motion.div>
                        </Box>
                    </Box>

                    <Box className="sicx-showcase-footer">
                        <Box>
                            <Typography className="sicx-footer-text">
                                LEARNING <span className="sicx-footer-highlight">•</span>{" "}
                                CHARACTER <span className="sicx-footer-highlight">•</span>{" "}
                                EXCELLENCE
                            </Typography>
                            <Typography className="sicx-footer-text">
                                Empowering the next generation through education.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.7,
                                color: "#b9dce9",
                                fontSize: 10,
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <BoltRounded sx={{ color: C.orange, fontSize: 17 }} />
                            POWERED BY POLU
                        </Box>
                    </Box>
                </Box>

                {/* MOBILE HEADER */}
                <Box className="sicx-mobile-brand">
                    <Box className="sicx-brand">
                        <Box className="sicx-brand-mark">
                            <SchoolRounded sx={{ fontSize: 25 }} />
                        </Box>

                        <Box>
                            <Typography className="sicx-brand-title">
                                Jana L. Williams Educational Foundation
                            </Typography>
                            <Typography className="sicx-brand-subtitle">
                                SCHOOL SYSTEM
                            </Typography>
                        </Box>
                    </Box>

                    <Box className="sicx-mobile-portal">
                        POLU PORTAL
                    </Box>
                </Box>

                {/* LOGIN EXPERIENCE */}
                <Box className="sicx-form-side">
                    <motion.div
                        className="sicx-form-inner"
                        initial={reduceMotion ? false : { opacity: 0, x: 15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.45, ease: "easeOut" }}
                    >
                        <Box className="sicx-form-topline">
                            <Box className="sicx-portal-label">
                                <SecurityRounded sx={{ fontSize: 17, color: C.blue }} />
                                SECURE PORTAL
                            </Box>

                            <Box className="sicx-online">
                                <Box className="sicx-online-dot" />
                                SYSTEM ACCESS
                            </Box>
                        </Box>

                        <Typography component="h1" className="sicx-form-heading">
                            Welcome back<span style={{ color: C.orange }}>.</span>
                        </Typography>

                        <Typography className="sicx-form-description">
                            Sign in with your registered school account to
                            continue to your personalized dashboard.
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3.2 }}>
                            <Typography
                                sx={{
                                    mb: 0.8,
                                    color: "#34485e",
                                    fontSize: 12,
                                    fontWeight: 850,
                                }}
                            >
                                Email address
                            </Typography>

                            <TextField
                                className="sicx-field"
                                fullWidth
                                placeholder="you@example.com"
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                required
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                inputProps={{ spellCheck: false }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailRounded
                                                sx={{ color: "#8ba0b2", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mt: "0 !important" }}
                            />

                            <Box
                                sx={{
                                    mt: 2.5,
                                    mb: 0.8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#34485e",
                                        fontSize: 12,
                                        fontWeight: 850,
                                    }}
                                >
                                    Password
                                </Typography>

                                <Link
                                    to="/forgotPassword"
                                    style={{
                                        color: C.blue,
                                        fontSize: 11,
                                        fontWeight: 850,
                                    }}
                                >
                                    Forgot password?
                                </Link>
                            </Box>

                            <TextField
                                className="sicx-field"
                                fullWidth
                                placeholder="Enter your password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                                autoComplete="current-password"
                                sx={{ mt: "0 !important" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockRounded
                                                sx={{ color: "#8ba0b2", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    setShowPassword((previous) => !previous)
                                                }
                                                edge="end"
                                                aria-label={
                                                    showPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                            >
                                                {showPassword ? (
                                                    <VisibilityOffRounded
                                                        sx={{ color: "#8192a4", fontSize: 20 }}
                                                    />
                                                ) : (
                                                    <VisibilityRounded
                                                        sx={{ color: "#8192a4", fontSize: 20 }}
                                                    />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                className="sicx-submit"
                                type="submit"
                                fullWidth
                                disabled={loading}
                                sx={{ mt: 3.2 }}
                                endIcon={
                                    !loading ? (
                                        <ArrowForwardRounded />
                                    ) : null
                                }
                            >
                                {loading ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.2,
                                        }}
                                    >
                                        <CircularProgress
                                            size={19}
                                            thickness={5}
                                            sx={{ color: "#fff" }}
                                        />
                                        Authenticating...
                                    </Box>
                                ) : (
                                    "Sign in to your account"
                                )}
                            </Button>

                            <Button
                                className="sicx-home-button"
                                fullWidth
                                onClick={() => navigate("/")}
                                startIcon={<ArrowBackRounded sx={{ fontSize: 17 }} />}
                                sx={{ mt: 1.3, py: 1.1 }}
                            >
                                Return to school homepage
                            </Button>
                        </Box>

                        <Divider sx={{ my: 2.8, borderColor: "#e5edf2" }} />

                        <Box className="sicx-trust-row">
                            <Box className="sicx-trust-icon">
                                <VerifiedRounded sx={{ fontSize: 21 }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography className="sicx-trust-title">
                                    Your account, protected.
                                </Typography>
                                <Typography className="sicx-trust-copy">
                                    Sign in using your registered credentials.
                                    Never share your password with anyone.
                                </Typography>
                            </Box>
                        </Box>

                        <Typography
                            sx={{
                                mt: 3,
                                color: "#98a5b3",
                                fontSize: 10,
                                lineHeight: 1.8,
                                textAlign: "center",
                            }}
                        >
                            © {new Date().getFullYear()} {BRAND}
                            <br />
                            School Management Portal · Powered by POLU
                        </Typography>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;